import { PGlite } from "@electric-sql/pglite";
import { sortBy } from "lodash-es";
import { logger } from "@/modules/logger";
import { SQLString } from "@/domains/database/types";

export interface Migration {
  version: string;
  sql: SQLString;
}

/**
 * Following conventions for how to manually manage migrations.
 *
 * We have a _migrations table where we track which ones have been applied on this device.
 *
 * See the `./migrations/index.ts` for more information.
 */
export async function runMigrations(db: PGlite, migrations: Migration[]): Promise<void> {
  // Create migrations table
  await db.query(`
    CREATE TABLE IF NOT EXISTS _migrations (
      version TEXT PRIMARY KEY,
      applied_at TEXT NOT NULL
    )
  `);

  // Get applied migrations
  const result = await db.query("SELECT version FROM _migrations");
  const appliedVersions = new Set(result.rows.map(row => (row as { version: string }).version));

  // Run pending migrations in order
  const sortedMigrations = sortBy(migrations, "version");

  for (const migration of sortedMigrations) {
    if (appliedVersions.has(migration.version)) {
      continue;
    }

    logger.info({
      message: `[Migrations] Applying migration ${migration.version}`,
    });

    // Split and execute each SQL statement separately within a transaction
    const statements = migration.sql
      .split(";")
      .map(stmt => stmt.trim())
      .filter(stmt => stmt.length > 0);

    await db.query("BEGIN");
    try {
      for (const statement of statements) {
        await db.query(statement);
      }

      await db.query("INSERT INTO _migrations (version, applied_at) VALUES ($1, $2)", [
        migration.version,
        new Date().toISOString(),
      ]);

      await db.query("COMMIT");
    } catch (err) {
      // Rollback best-effort to avoid partial application
      try {
        await db.query("ROLLBACK");
      } catch (_) {
        // ignore rollback errors
      }

      throw err;
    }
  }
}
